import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { OdometerScanButton } from './OdometerScanner';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { avisoKm, parseKm } from '../utils/km';
import { cn } from '../lib/utils';

/**
 * Km del tablero de la OT (opcional en todos lados desde el 16/10/2026).
 * Campo + escáner de odómetro (solo en celular o tablet) + aviso si no cierra
 * contra la ficha.
 */
export const KmTableroInput: React.FC<{
  id?: string;
  value: string;
  onChange: (v: string) => void;
  kmFicha?: number | null;
  autoFocus?: boolean;
  error?: boolean;
  className?: string;
}> = ({ id = 'km-tablero', value, onChange, kmFicha, autoFocus, error, className }) => {
  const aviso = avisoKm(value, kmFicha);
  const ficha = parseKm(kmFicha);
  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode="numeric"
          autoFocus={autoFocus}
          className={cn('h-9', error && 'border-destructive focus-visible:ring-destructive')}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder={ficha ? `ficha: ${ficha.toLocaleString('es-AR')}` : 'km del tablero'}
        />
        <OdometerScanButton onKm={(km) => onChange(String(km))} kmReferencia={ficha || undefined} className="h-9 shrink-0" />
      </div>
      {aviso && (
        <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
          <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />{aviso}
        </p>
      )}
    </div>
  );
};

/**
 * Diálogo que pide el km de una OT que no lo tiene. Con `onSkip` (Completar
 * trabajo en Taller, hoy el único uso) el km es opcional: aparece un tercer
 * botón para seguir sin cargarlo.
 */
export const KmIngresoDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titulo: string;
  vehiculo?: string;
  kmFicha?: number | null;
  confirmLabel?: string;
  onConfirm: (km: number) => Promise<void> | void;
  /** Si viene, el km es opcional: botón para seguir sin cargarlo */
  onSkip?: () => Promise<void> | void;
  skipLabel?: string;
}> = ({ open, onOpenChange, titulo, vehiculo, kmFicha, confirmLabel = 'Ingresar', onConfirm, onSkip, skipLabel = 'Seguir sin km' }) => {
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [intento, setIntento] = useState(false);
  useEffect(() => { if (open) { setValor(''); setIntento(false); setGuardando(false); } }, [open]);
  const km = parseKm(valor);

  const confirmar = async (e?: React.FormEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIntento(true);
    if (km == null) return;
    setGuardando(true);
    try {
      await onConfirm(km);
      onOpenChange(false);
    } finally {
      setGuardando(false);
    }
  };

  const saltear = async () => {
    if (!onSkip) return;
    setGuardando(true);
    try {
      await onSkip();
      onOpenChange(false);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !guardando && onOpenChange(v)}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={confirmar}>
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogDescription>
              {vehiculo ? <><span className="font-medium text-foreground">{vehiculo}</span> · </> : null}
              {onSkip
                ? 'Esta OT no tiene los km del tablero. Si los tenés a mano, cargalos: salen en el remito y el informe de servicio. Si no, podés completar sin km.'
                : 'Anotá los km del tablero. Quedan en la OT, el remito y el informe de servicio.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="km-ingreso" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Km del tablero{onSkip ? ' (opcional)' : ''}</Label>
            <KmTableroInput id="km-ingreso" value={valor} onChange={setValor} kmFicha={kmFicha} autoFocus error={intento && km == null} />
            {intento && km == null && <p className="mt-1 text-xs text-destructive">{onSkip ? 'Cargá los km, o usá "' + skipLabel + '".' : 'Cargá los km para seguir.'}</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
            {onSkip && (
              <Button type="button" variant="secondary" onClick={saltear} disabled={guardando}>{skipLabel}</Button>
            )}
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : confirmLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
